import { faEdit } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input, Label } from '@repo/videa-components/src/index';
import { cn } from '@repo/videa-components/src/lib/utils';

import { CopyButton } from '../../../components/CopyButton';

const EDITING_FIELD_ACTIVE_STYLES =
  '!vai-border-blue-500 focus-visible:vai-ring-2 focus-visible:vai-ring-blue-500 focus-visible:vai-border-blue-500';

interface SecurityAnswerFieldProps {
  fieldId: 'securityAnswer1' | 'securityAnswer2' | 'securityAnswer3';
  questionNumber: 1 | 2 | 3;
  questionValue: string;
  answerValue: string;
  tempAnswerValue: string;
  editingField: string | null;
  isSaving: boolean;
  isCopySuccess: string | null;
  onStartEdit: (fieldId: string) => void;
  onCancelEdit: () => void;
  onSave: () => Promise<void>;
  onCopy: (value: string, fieldId: string) => void;
  onTempAnswerChange: (value: string) => void;
}

export const SecurityAnswerField = ({
  fieldId,
  questionNumber,
  questionValue,
  answerValue,
  tempAnswerValue,
  editingField,
  isSaving,
  isCopySuccess,
  onStartEdit,
  onCancelEdit,
  onSave,
  onCopy,
  onTempAnswerChange,
}: SecurityAnswerFieldProps) => {
  const { t } = useLingui();
  const isEditing = editingField === fieldId;
  const isDisabled = !questionValue || questionValue === 'None';
  const groupClass = `vai-group/answer${questionNumber}`;

  return (
    <div className="vai-group">
      <Label htmlFor={fieldId}>
        <Trans>Answer</Trans>
      </Label>
      <div className="vai-flex vai-items-center vai-gap-2">
        <div className={`vai-relative vai-flex-1 ${groupClass}`}>
          <Input
            id={fieldId}
            type="text"
            value={isEditing ? tempAnswerValue : answerValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onTempAnswerChange(e.target.value)
            }
            placeholder={t`Enter answer`}
            readOnly={!isEditing}
            disabled={isDisabled}
            className={cn(
              'vai-pr-20',
              isEditing && EDITING_FIELD_ACTIVE_STYLES,
              isDisabled && 'vai-opacity-50 vai-cursor-not-allowed',
            )}
          />
          <div className="vai-absolute vai-right-2 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-flex vai-gap-1">
            {!isEditing && (
              <>
                {questionValue && questionValue !== 'None' && (
                  <>
                    <button
                      type="button"
                      className={`vai-w-8 vai-h-8 vai-flex vai-items-center vai-justify-center hover:vai-bg-gray-100 vai-rounded vai-opacity-0 group-hover/answer${questionNumber}:vai-opacity-100 vai-transition-opacity`}
                      onClick={() => onStartEdit(fieldId)}
                      title={t`Edit answer`}
                    >
                      <FontAwesomeIcon
                        icon={faEdit}
                        className="vai-w-4 vai-h-4 vai-text-muted-foreground"
                      />
                    </button>
                    <CopyButton
                      value={answerValue}
                      fieldId={fieldId}
                      isCopied={isCopySuccess === fieldId}
                      onCopy={onCopy}
                      copyLabel={t`Copy security answer ${questionNumber}`}
                      isAbsolutePositioned={false}
                    />
                  </>
                )}
              </>
            )}
            {isEditing && (
              <CopyButton
                value={tempAnswerValue}
                fieldId={fieldId}
                isCopied={isCopySuccess === fieldId}
                onCopy={onCopy}
                copyLabel={t`Copy security answer ${questionNumber}`}
                isAbsolutePositioned={false}
              />
            )}
          </div>
        </div>
        {isEditing && (
          <div className="vai-flex vai-gap-2 vai-shrink-0">
            <Button size="sm" variant="outline" onClick={onCancelEdit}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              size="sm"
              onClick={onSave}
              disabled={isSaving}
            >
              {isSaving ? <Trans>Saving...</Trans> : <Trans>Save</Trans>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
